import { S } from '../app/app_state.js?v=20260817c';
import { cloneSystemData } from '../../plugin-contracts/system_data_contract.mjs';
import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';
import { markProjectModelChanged } from '../project/project_revision_service.js';

const scopes = new Map();
let historyGeneration = 0;
export const independentHistoryGeneration = () => historyGeneration;
const copy = value => value === undefined ? undefined : cloneSystemData(value);
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function registerSystemHistoryScope(contract, isActive, canRestore = () => true) {
  const registration = { contract, isActive, canRestore };
  scopes.set(contract.systemId, registration);
  return () => { if (scopes.get(contract.systemId) === registration) scopes.delete(contract.systemId); };
}
export function activeSystemHistoryOwner() {
  return [...scopes].find(([, scope]) => scope.isActive())?.[0] || null;
}
export function resetSystemHistories() {
  historyGeneration += 1;
  if (S.history) { delete S.history.systems; delete S.history.orderData; }
}
export function systemDataSnapshot(contract, project = S.project) {
  let value = project;
  for (const key of contract.path) value = value?.[key];
  return { data: value === undefined ? null : copy(value), present: value !== undefined,
    version: project?.systemDataVersions?.[contract.systemId] ?? null };
}
export function assignSystemDataSnapshot(contract, snapshot, project = S.project) {
  let target = project;
  for (const key of contract.path.slice(0, -1)) {
    if (target[key] !== undefined && (!target[key] || typeof target[key] !== 'object' || Array.isArray(target[key]))) {
      throw new TypeError('Systemdatenpfad kann keinen fremden Wert ersetzen.');
    }
    target[key] = { ...target[key] };
    target = target[key];
  }
  if (snapshot.present) target[contract.path.at(-1)] = copy(snapshot.data);
  else delete target[contract.path.at(-1)];
  const versions = { ...project.systemDataVersions };
  if (snapshot.version === null) delete versions[contract.systemId];
  else versions[contract.systemId] = snapshot.version;
  if (Object.keys(versions).length) project.systemDataVersions = versions;
  else delete project.systemDataVersions;
}
export function orderDataSnapshot(owner, project = S.project) {
  return copy(project?.systemOrderData?.[owner] ?? null);
}
export function assignOrderDataSnapshot(owner, value, project = S.project) {
  const store = { ...project.systemOrderData };
  if (value === null) delete store[owner]; else store[owner] = copy(value);
  if (Object.keys(store).length) project.systemOrderData = store; else delete project.systemOrderData;
}
// A legacy whole-sheet restore must not overwrite an independent owner or order.
export function preserveIndependentHistoryData(current, restored, generation = historyGeneration) {
  if (generation !== historyGeneration) { resetSystemHistories(); return; }
  if (Object.hasOwn(current, 'systemOrderData')) restored.systemOrderData = copy(current.systemOrderData);
  else delete restored.systemOrderData;
  for (const { contract } of scopes.values()) assignSystemDataSnapshot(contract, systemDataSnapshot(contract, current), restored);
}
function bank(owner, kind, create = false) {
  if (!S.history) return null;
  if (create) {
    S.history[kind] ||= {};
    S.history[kind][owner] ||= { undo: [], redo: [] };
  }
  return S.history[kind]?.[owner] || null;
}
function capture(contract, kind) {
  return kind === 'orderData' ? orderDataSnapshot(contract.systemId) : systemDataSnapshot(contract);
}
function dirty(label) {
  if (S.file) S.file.dirty = true;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ks:project-dirty', { detail: { label } }));
  markProjectModelChanged(label);
}
export function recordSystemUndo(contract, label, kind = 'systems') {
  if (isPreparingProjectMutation()) throw new Error('Der Host besitzt die History dieser Projektmutation.');
  const history = bank(contract.systemId, kind, true);
  const previous = { redo: history.redo, dirty: S.file?.dirty };
  const entry = { label, before: capture(contract, kind), after: null };
  history.undo.push(entry);
  const evicted = history.undo.length > (S.history.limit || 80) ? history.undo.shift() : null;
  history.redo = [];
  dirty(label);
  return { ...previous, history, entry, contract, kind, evicted };
}
export function completeSystemUndo(token, orderChange = null) {
  token.entry.after = capture(token.contract, token.kind);
  if (orderChange) token.entry.orderChange = copy(orderChange);
}
export function cancelSystemUndo(token) {
  if (bank(token.contract.systemId, token.kind) !== token.history || token.history.undo.at(-1) !== token.entry) return;
  token.history.undo.pop(); token.history.redo = token.redo;
  if (token.evicted) token.history.undo.unshift(token.evicted);
  if (token.kind === 'systems') assignSystemDataSnapshot(token.contract, token.entry.before);
  if (S.file) S.file.dirty = token.dirty;
}
function available(contract, kind, direction) {
  const entry = bank(contract.systemId, kind)?.[direction]?.at(-1);
  if (!entry) return false;
  const expected = direction === 'undo' ? 'after' : 'before';
  return equal(capture(contract, kind), entry[expected])
    && (!entry.orderChange || equal(orderDataSnapshot(contract.systemId), entry.orderChange[expected]));
}
export function systemHistoryAvailable(owner, direction, kind = 'systems') {
  const contract = scopes.get(owner)?.contract;
  return Boolean(contract && scopes.get(owner).canRestore() && available(contract, kind, direction));
}
export function restoreSystemHistory(owner, direction, kind = 'systems') {
  const contract = scopes.get(owner)?.contract;
  if (!contract || !scopes.get(owner).canRestore() || !available(contract, kind, direction)) return false;
  const history = bank(owner, kind), entry = history[direction].pop();
  const destination = direction === 'undo' ? 'before' : 'after';
  if (kind === 'orderData') assignOrderDataSnapshot(owner, entry[destination]);
  else assignSystemDataSnapshot(contract, entry[destination]);
  if (entry.orderChange) assignOrderDataSnapshot(owner, entry.orderChange[destination]);
  history[direction === 'undo' ? 'redo' : 'undo'].push(entry);
  dirty(`${direction}:${entry.label}`);
  return true;
}
